import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/kibo-ui/combobox';
import { cn } from '@/lib/utils';
import {
  CheckIcon,
  ChevronsUpDownIcon,
  RectangleHorizontalIcon,
  RectangleVerticalIcon,
  SquareIcon,
} from 'lucide-react';
import { useState } from 'react';
import { usePreviewCanvas } from '@/hooks/use-preview-canvas';

type ImageSizeSelectorProps = {
  id?: string;
  value: string;
  options: string[];
  width?: number | string | "auto";
  className?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  // Optional notes for each option. If not provided, a default note (aspect ratio) will be shown for pixel sizes,
  // and a friendly note for 'match'.
  notes?: Record<string, string>;
};

const getIcon = (option: string) => {
  // Special pseudo-option for image-to-image: match input image size
  if (option === 'match') {
    return <SquareIcon size={16} className="text-muted-foreground" />;
  }
  // Auto: model did not provide sizes / let provider decide
  if (option === 'Auto') {
    return <SquareIcon size={16} className="text-muted-foreground" />;
  }

  const [width, height] = option.split('x').map(Number);

  if (width === height) {
    return <SquareIcon size={16} className="text-muted-foreground" />;
  }

  if (width > height) {
    return (
      <RectangleHorizontalIcon size={16} className="text-muted-foreground" />
    );
  }

  return <RectangleVerticalIcon size={16} className="text-muted-foreground" />;
};

const getLabel = (option: string) => {
  if (option === 'match') {
    // Show as "Auto" in UI while keeping underlying value as "match"
    return <div className="whitespace-nowrap">Auto</div>;
  }
  if (option === 'Auto') {
    return <div className="whitespace-nowrap">Auto</div>;
  }

  const [width, height] = option.split('x').map(Number);

  return (
    <div className="flex items-center gap-1 whitespace-nowrap">
      <span>{width}</span>
      <span className="text-muted-foreground">&times;</span>
      <span>{height}</span>
    </div>
  );
};

export const ImageSizeSelector = ({
  id,
  value,
  width = 200,
  options,
  className,
  onChange,
  disabled,
  notes,
}: ImageSizeSelectorProps) => {
  const [open, setOpen] = useState(false);
  const isPreview = usePreviewCanvas();
  const effectiveDisabled = Boolean(disabled || isPreview);

  const getNote = (option: string) => {
    return notes && typeof notes[option] === 'string' ? notes[option] : undefined;
  };

  return (
    <Combobox
      open={open}
      onOpenChange={(next) => {
        if (effectiveDisabled) return;
        setOpen(next);
      }}
      data={options.map((option) => ({
        label: option,
        value: option,
      }))}
      type="size"
      value={value}
      onValueChange={onChange}
    >
      <ComboboxTrigger 
        id={id} 
        variant="ghost" 
        className={cn("rounded-xl h-9 px-3 hover:bg-sidebar-accent transition-all border-none font-medium", className)} 
        style={width === "auto" ? {} : { width }} 
        disabled={effectiveDisabled}
      >
        <div className="flex items-center justify-between gap-2 w-full">
          <div className="flex items-center gap-2 overflow-hidden">
            {getIcon(value)}
            <span className="truncate">{getLabel(value)}</span>
          </div>
          <ChevronsUpDownIcon size={14} className="shrink-0 text-muted-foreground opacity-50" />
        </div>
      </ComboboxTrigger>
      <ComboboxContent
        className={cn('p-0', className)}
        popoverOptions={{ className: 'fm-toolbar-surface rounded-2xl border border-sidebar-border bg-sidebar/95 backdrop-blur-xl p-1.5 shadow-none' }}
      >
        <ComboboxList className="custom-scrollbar">
          <ComboboxEmpty />
          <ComboboxGroup className="p-1">
            {options.map((option) => (
              <ComboboxItem
                key={option}
                value={option}
                onSelect={() => {
                  onChange?.(option);
                  setOpen(false);
                }}
                className="rounded-lg py-2 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer gap-2"
              >
                {getIcon(option)}
                {getLabel(option)}
                {(() => {
                  const note = getNote(option);
                  return note ? (
                    <span className="text-[10px] text-muted-foreground/60 ml-1">{note}</span>
                  ) : null;
                })()}
                <CheckIcon
                  className={cn(
                    'ml-auto size-4 text-primary',
                    value === option ? 'opacity-100' : 'opacity-0'
                  )}
                />
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};
